/**
 * What release this tab is actually RUNNING.
 *
 * Vite stamps `__APP_VERSION__` in at build time from backend/package.json —
 * the same file the server reports through /api/auth/session-info. A tab that
 * has been open across a deploy is still executing its original bundle, so the
 * two numbers diverge, and that divergence is the signal that the user needs a
 * refresh (see `useAppVersion`). Nothing else can detect this: the HTML and the
 * API are served no-store, but JavaScript already in memory never re-reads
 * itself.
 */
export const BUILD_VERSION: string =
  typeof __APP_VERSION__ === 'string' && __APP_VERSION__ ? __APP_VERSION__ : 'unknown';

/** A version string we can meaningfully compare (the server sends 'unknown' when it can't tell). */
export function isKnownVersion(v: string | null | undefined): v is string {
  return !!v && v !== 'unknown';
}

/**
 * Does this tab need a refresh?
 *
 * A plain inequality, deliberately: a rollback needs the same refresh a
 * roll-forward does. Either side being unknown (an anonymous caller gets no
 * version, and a build outside Vite has no stamp) means we can't tell — and an
 * unprovoked "update available" on every page load would be worse than
 * missing one, so we stay quiet.
 */
export function hasUpdate(build: string | null | undefined, server: string | null | undefined): boolean {
  return isKnownVersion(build) && isKnownVersion(server) && build !== server;
}

/**
 * Reload onto the freshly deployed build. index.html, sw.js and the manifest
 * are all served no-store and Vite's bundles are content-hashed, so a plain
 * reload is already enough — but a browser that kept a copy anyway (an
 * intermediate proxy, an aggressive mobile cache) would keep the user stuck on
 * the old build forever, so drop the Cache Storage entries first and let the
 * service worker re-register from the new bundle.
 */
export async function reloadToLatest(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } catch {
    /* Cache Storage unavailable (private mode, old browser) — the reload still works */
  }
  window.location.reload();
}
